import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import Landing from "@/pages/Landing";
import Manifesto from "@/pages/Manifesto";
import Store from "@/pages/Store";
import IgLink from "@/pages/IgLink";
import AdminTickets from "@/pages/AdminTickets";
import AdminScan from "@/pages/AdminScan";
import AdminDashboard from "@/pages/AdminDashboard";
import Contact from "@/pages/Contact";
import Success from "@/pages/Success";
import Cancel from "@/pages/Cancel";
import QuickCheckout from "@/pages/QuickCheckout";
import CompCheckout from "@/pages/CompCheckout";
import Tour from "@/pages/Tour";
import Chronology from "@/pages/Chronology";
import Lyrics from "@/pages/Lyrics";
import Press from "@/pages/Press";
import Community from "@/pages/Community";
import QuickPayWhatYouCan from "@/pages/QuickPayWhatYouCan";
import VinylFulfill from "@/pages/VinylFulfill";
import Tickets from "@/pages/Tickets";
import ShowOverview from "@/pages/ShowOverview";
import Handout from "@/pages/Handout";
import TicketSwap from "@/pages/TicketSwap";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/state/CartContext";
import CartDrawer from "@/components/CartDrawer";
import BackdropPortrait from "@/components/BackdropPortrait";
import MovingOrb from "@/components/MovingOrb";
import VintageFilm from "@/components/VintageFilm";
import SiteGate from "@/components/SiteGate";
import ScrollToTop from "@/components/ScrollToTop";

/**
 * Gated layout — wraps every storefront / admin route with the VIP password
 * SiteGate. Quick-checkout `/buy/...` routes are deliberately outside this
 * so Linktree visitors can purchase with no password required.
 */
function GatedLayout() {
  return (
    <SiteGate>
      <BackdropPortrait />
      <MovingOrb />
      <Outlet />
      <CartDrawer />
    </SiteGate>
  );
}

export default function App() {
  return (
    <div className="App">
      <VintageFilm />
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Public deep-link routes (no SiteGate) — for Linktree / IG bio / QR posters */}
            <Route path="/buy/wav-pwyc" element={<QuickPayWhatYouCan />} />
            <Route path="/buy/:slug" element={<QuickCheckout />} />
            {/* Alias `/ticket/:slug` — same as /buy/ but reads cleaner for
                ticket links shared in emails / DMs (e.g. /ticket/london-artist). */}
            <Route path="/ticket/:slug" element={<QuickCheckout />} />
            {/* Single-use comp links — admin-minted, see /api/admin/comp-tokens. */}
            <Route path="/comp/:token" element={<CompCheckout />} />
            {/* All-cities discovery page — bio + every on-sale show. Linked
                from /store under the "Live" section heading. */}
            <Route path="/tour" element={<Tour />} />
            {/* Chronology — discography timeline. Linked from /store under
                the "Music" section heading. Public so it can be shared. */}
            <Route path="/chronology" element={<Chronology />} />
            {/* Lyrics — credits + lyric sheet booklet. Not linked publicly
                yet; the artist will surface it after Metamorph is released.
                Mirrors the PDF served at /api/admin/lyrics/pdf. */}
            <Route path="/lyrics" element={<Lyrics />} />
            {/* Press & radio listening room — unlisted, no public nav.
                Artist shares the URL directly with editors / programmers. */}
            <Route path="/press" element={<Press />} />
            {/* The fan / community portal — public, no gate. */}
            <Route path="/community" element={<Community />} />
            <Route path="/vinyl/fulfill/:token" element={<VinylFulfill />} />
            <Route path="/tickets" element={<Tickets />} />
            <Route path="/shows/:city" element={<ShowOverview />} />
            {/* Print-ready A5 handout — given to guests at the door. Front
                is a full-bleed portrait keepsake; back has GM's note + QR
                to vinyl + exclusive download. Public so it can be printed
                without the SiteGate password. */}
            <Route path="/print/handout" element={<Handout />} />
            {/* Self-serve ticket → vinyl + $40 refund swap for Melbourne &
                Adelaide reschedule guests. Public (email + ticket ref auth). */}
            <Route path="/swap-ticket" element={<TicketSwap />} />

            {/* Gated routes — SiteGate enforces the VIP password */}
            <Route element={<GatedLayout />}>
              <Route path="/" element={<Landing />} />
              <Route path="/manifesto" element={<Manifesto />} />
              <Route path="/store" element={<Store />} />
              <Route path="/purchase" element={<Store />} />
              <Route path="/purchase/pay" element={<Store />} />
              <Route path="/link" element={<IgLink />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/tickets" element={<AdminTickets />} />
              <Route path="/admin/scan" element={<AdminScan />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/success" element={<Success />} />
              <Route path="/cancel" element={<Cancel />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </CartProvider>
      <Toaster
        theme="dark"
        position="bottom-center"
        toastOptions={{
          style: {
            background: "#0c0c0c",
            border: "1px solid #1a1a1a",
            color: "#f3f2ee",
            fontFamily: "Outfit, sans-serif",
            borderRadius: 0,
          },
        }}
      />
    </div>
  );
}
